import { store } from "./store";

const API_BASE = "/api";
const DEBOUNCE_MS = 800;
const LOCAL_TS_KEY = "hr_a1_quiz_v1_ts";

let pushTimer: number | null = null;
let pulling = false;
let suspended = false;

export interface SyncResult {
  ok: boolean;
  authenticated: boolean;
  error?: string;
}

export async function checkAuth(): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/me.php`, { credentials: "include" });
    return r.ok;
  } catch {
    return false;
  }
}

export async function login(password: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch(`${API_BASE}/login.php`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (r.ok) return { ok: true };
    const j = await r.json().catch(() => ({}));
    return { ok: false, error: j.error || `Fehler ${r.status}` };
  } catch (e) {
    return { ok: false, error: "Netzwerkfehler" };
  }
}

export async function logout(): Promise<void> {
  suspended = true;
  try {
    await fetch(`${API_BASE}/logout.php`, { method: "POST", credentials: "include" });
  } catch {
    /* ignore */
  }
  localStorage.removeItem(LOCAL_TS_KEY);
}

export async function pullState(): Promise<SyncResult> {
  pulling = true;
  try {
    const r = await fetch(`${API_BASE}/state.php`, { credentials: "include" });
    if (r.status === 401) return { ok: false, authenticated: false };
    if (!r.ok) return { ok: false, authenticated: true, error: `HTTP ${r.status}` };
    const j = await r.json();
    const serverTs: number = j.updated_at || 0;
    const localTs = parseInt(localStorage.getItem(LOCAL_TS_KEY) || "0", 10);
    if (j.state && serverTs > localTs) {
      const ok = store.import(JSON.stringify(j.state));
      if (ok) localStorage.setItem(LOCAL_TS_KEY, String(serverTs));
    }
    return { ok: true, authenticated: true };
  } catch (e) {
    return { ok: false, authenticated: true, error: "Netzwerkfehler" };
  } finally {
    pulling = false;
  }
}

export function scheduleSync(): void {
  if (pulling || suspended) return;
  if (pushTimer !== null) window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(pushState, DEBOUNCE_MS);
}

async function pushState(): Promise<void> {
  pushTimer = null;
  try {
    const r = await fetch(`${API_BASE}/state.php`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state: store.state }),
    });
    if (r.ok) {
      const j = await r.json();
      const ts: number = j.updated_at || Math.floor(Date.now() / 1000);
      localStorage.setItem(LOCAL_TS_KEY, String(ts));
    }
  } catch {
    /* retry next save */
  }
}

export function flushSync(): Promise<void> {
  if (pushTimer !== null) {
    window.clearTimeout(pushTimer);
    pushTimer = null;
    return pushState();
  }
  return Promise.resolve();
}

window.addEventListener("beforeunload", () => {
  if (pushTimer !== null) {
    navigator.sendBeacon(
      `${API_BASE}/state.php`,
      new Blob([JSON.stringify({ state: store.state })], { type: "application/json" })
    );
  }
});
